import { useCallback } from 'react';
import useRouter from '~/client/hooks/router/useRouter';
import useCurrentUser from '~/client/hooks/user/useCurrentUser';
import useToastContext from '~/client/layout/DefaultLayout/LayoutContext/Toast/useToastContext';
import { useTournamentClaimPrizeMutation } from '../../../graphql/TournamentClaimPrizeMutation.generated';
import type { TournamentFragment } from '../../../graphql/TournamentFragment.generated';

type Props = {
  scoreId: string;
  toggleDialog: () => void;
  tournament?: TournamentFragment;
};

const useClaimPrize = ({ scoreId, toggleDialog, tournament }: Props) => {
  const router = useRouter();
  const { user } = useCurrentUser();
  const [claimPrize, { loading }] = useTournamentClaimPrizeMutation();
  const { showToast } = useToastContext();
  const prize = getPrizeForPosition(tournament);
  const isAutomaticallyReleased = prize?.automaticallyReleased;

  const handleClaim = useCallback(async () => {
    if (!user?.id) {
      router.push('/login');
    }
    const { data } = await claimPrize({
      variables: {
        scoreId,
      },
    });
    if (!data?.casinoTournamentsPublicClaimPrizeMutation?.errors) {
      if (isAutomaticallyReleased) {
        showToast({
          message: 'rewards.new_reward_added',
          translateValues: {
            reward: prize?.name ?? '',
          },
          variant: 'success',
          position: 'bottom',
          timeout: 5000,
        });
      } else {
        showToast({
          message: 'general.success',
          variant: 'success',
          position: 'bottom',
          timeout: 5000,
        });
        toggleDialog();
      }
    }
  }, [
    claimPrize,
    router,
    scoreId,
    showToast,
    toggleDialog,
    user?.id,
    isAutomaticallyReleased,
    prize?.name,
  ]);

  return { loading, handleClaim, isAutomaticallyReleased };
};

const getPrizeForPosition = (tournament?: TournamentFragment) => {
  const myPosition = tournament?.myScore?.position;
  const myPrize = tournament?.prizes?.filter(
    (prize) => prize.forPosition === myPosition,
  );
  return myPrize?.[0];
};

export default useClaimPrize;
